import { Arrows, General, Maps, Media, Security } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import type { CampaignTemplateOption } from './CampaignTemplateFilterSelect';
import { TEMPLATE_ALL_OPTION_VALUE, TEMPLATE_ANY_OPTION_VALUE } from './CampaignTemplateFilterSelect';
import FilterSelect, { type SelectOptionGroup } from './FilterSelect';
import type { SelectOption } from './Select';
import type { Size, UpdateStatus } from './types';
import type { ReactNode } from 'react';

export type CampaignOverrideMode = 'skip_existing' | 'force_override' | 'smart_override';

export const OVERRIDE_MODE_OPTIONS = [
  {
    value: 'smart_override',
    label: 'Smart',
    icon: <Media.Lightbulb className="size-3.5" />,
    description: 'Same campaign → skip · Different → replace',
  },
  {
    value: 'skip_existing',
    label: 'Keep',
    icon: <Security.Shield01 className="size-3.5" />,
    description: 'Skip anyone already in a campaign',
  },
  {
    value: 'force_override',
    label: 'Force',
    icon: <Arrows.ArrowSquareDown className="size-3.5" />,
    description: 'Replace any existing campaign',
  },
];

/**
 * POS-2455 "Resume from last left off" — surfaced as a two-option select so it
 * reads exactly like the override-mode control beside it. `resume` (the
 * default) skips steps the enrolling rep already sent each contact and refuses
 * fully-sent contacts; `fresh` re-sends everything from the top.
 */
export const RESUME_PRIOR_STEPS_OPTIONS = [
  {
    value: 'resume',
    label: 'Resume',
    icon: <Media.SkipForward className="size-3.5" />,
    description: 'Skip steps they already received from you',
  },
  {
    value: 'fresh',
    label: 'Fresh',
    icon: <Arrows.RefreshCw04 className="size-3.5" />,
    description: 'Re-send every step from the top',
  },
];

export const RESUME_PRIOR_STEPS_TOOLTIPS: Record<'resume' | 'fresh', string> = {
  resume: `Resume from last left off:
• Steps they already received from you → skipped
• Everything already sent → contact is not re-added`,
  fresh: `Start fresh:
• Re-send every step, even ones they already received`,
};

export const OVERRIDE_MODE_TOOLTIPS: Record<CampaignOverrideMode, string> = {
  smart_override: `Smart:
• Already in this campaign → do nothing
• In a different campaign → stop it, start this one
• Not in any campaign → start this one`,
  skip_existing: `Keep:
• Already in any campaign → skip them
• Not in any campaign → start this one`,
  force_override: `Force:
• Stop any existing campaign
• Start this one regardless`,
};

export type CampaignTemplateFilterSelectWithOverrideProps = {
  templates: CampaignTemplateOption[];
  renderOwnerIcon?: (template: CampaignTemplateOption) => ReactNode;
  value?: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  overrideMode: CampaignOverrideMode;
  onOverrideModeChange: (mode: CampaignOverrideMode) => void;
  placeholder?: string;
  className?: string;
  status?: UpdateStatus;
  includeRemoveOption?: boolean;
  includeFilterOptions?: boolean;
  size?: Size;
  color?: ColorName;
  tone?: Style;
  onClear?: () => void;
  /**
   * POS-2455 "Resume from last left off". When BOTH props are provided a
   * Resume/Fresh select is rendered beside the override-mode control; when
   * omitted, the surface silently gets the safe server default (resume).
   */
  resumePriorSteps?: boolean;
  onResumePriorStepsChange?: (resume: boolean) => void;
};

export default function CampaignTemplateFilterSelectWithOverride({
  templates,
  renderOwnerIcon,
  value,
  defaultValue,
  onChange,
  overrideMode,
  onOverrideModeChange,
  placeholder = 'Select campaign',
  className,
  status,
  includeRemoveOption,
  includeFilterOptions,
  size,
  color,
  tone,
  onClear,
  resumePriorSteps,
  onResumePriorStepsChange,
}: CampaignTemplateFilterSelectWithOverrideProps) {
  const selectedTemplate = templates.find((t) => t.id === value);
  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const toSelectOption = (template: CampaignTemplateOption): SelectOption => {
    const hasErrors = (template.errorCount ?? 0) > 0;
    const errorDescription = hasErrors
      ? `Campaign has ${template.errorCount} ${template.errorCount === 1 ? 'error' : 'errors'}`
      : undefined;
    return {
      value: template.id,
      label: template.name,
      icon: hasErrors ? <General.HelpCircle className="text-fair-pink-600" /> : (renderOwnerIcon?.(template) ?? <Maps.Rocket />),
      description: errorDescription,
    };
  };

  const myTemplates = templates.filter((t) => t.isMine);
  const teamTemplates = templates.filter((t) => !t.isMine);

  const groups: (SelectOption | SelectOptionGroup)[] = [];
  if (includeRemoveOption) {
    groups.push({ value: '', label: 'Remove from campaign', icon: <General.Trash01 />, color: 'red' as const });
  }
  if (includeFilterOptions) {
    groups.push({ value: TEMPLATE_ALL_OPTION_VALUE, label: 'All Campaigns', icon: <Maps.Rocket /> });
    groups.push({ value: TEMPLATE_ANY_OPTION_VALUE, label: 'Any Campaign', icon: <Maps.Rocket /> });
  }
  if (myTemplates.length > 0) {
    groups.push({ label: 'My Campaigns', options: myTemplates.map(toSelectOption) });
  }
  if (teamTemplates.length > 0) {
    groups.push({ label: 'Team Campaigns', options: teamTemplates.map(toSelectOption) });
  }

  const isFilterOption = value === TEMPLATE_ALL_OPTION_VALUE || value === TEMPLATE_ANY_OPTION_VALUE || value == null;
  const triggerIcon = isFilterOption ? <Maps.Rocket /> : (selectedTemplate ? (renderOwnerIcon?.(selectedTemplate) ?? <Maps.Rocket />) : <Maps.Rocket />);

  return (
    <ButtonGroup size={size} color={color}
      tone={tone}>
      <FilterSelect
        value={overrideMode}
        options={OVERRIDE_MODE_OPTIONS}
        onChange={(v) => onOverrideModeChange(v as CampaignOverrideMode)}
        tooltip={OVERRIDE_MODE_TOOLTIPS[overrideMode]}
        tooltipMaxWidth={340}
        filterable={false}
        showText={false}
        autoWidth
        popoverMinWidth={320}
        popoverMaxWidth={420}
      />
      {resumePriorSteps != null && onResumePriorStepsChange != null && (
        <FilterSelect
          value={resumePriorSteps ? 'resume' : 'fresh'}
          options={RESUME_PRIOR_STEPS_OPTIONS}
          onChange={(v) => onResumePriorStepsChange(v === 'resume')}
          tooltip={RESUME_PRIOR_STEPS_TOOLTIPS[resumePriorSteps ? 'resume' : 'fresh']}
          tooltipMaxWidth={340}
          filterable={false}
          showText={false}
          autoWidth
          popoverMinWidth={300}
          popoverMaxWidth={420}
        />
      )}
      <FilterSelect
        icon={triggerIcon}
        options={groups}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
        status={status}
        autoWidth
      />
      {showClearButton && (
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      )}
    </ButtonGroup>
  );
}
